
import { Controller } from "@hotwired/stimulus";
import createLiveReportingChannel from "../channels/live_reporting_channel";

/**
 * Stimulus controller for the live reporting stream.
 * Manages ActionCable subscription, handles real-time updates, and exposes pause/resume controls.
 *
 * @class LiveReportingStreamController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * Survey ID and live state values passed from the DOM.
   * @type {{surveyId: Number, live: Boolean}}
   */
  static values = { surveyId: Number, live: Boolean };

  /**
   * Called when the controller is connected to the DOM.
   * Sets up ActionCable subscription and exposes global references for controls.
   */
  connect() {
    // Create the ActionCable subscription for this survey
    this.subscription = createLiveReportingChannel(this.surveyIdValue, {
      received: (data) => this.handleBroadcast(data),
      paused: (position) => this.handlePaused(position),
      resumed: () => this.handleResumed()
    });

    // Store reference for global access (for pause/resume buttons)
    window.liveReportingSubscription = this.subscription;
    window.liveReportingController = this;
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up the ActionCable subscription and global references.
   */
  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    window.liveReportingSubscription = null;
    window.liveReportingController = null;
  }

  /**
   * Notify connection monitor that we received data from ActionCable.
   * This resets the staleness timer.
   */
  notifyConnectionActivity() {
    this.element.dispatchEvent(new CustomEvent('cable:received', { bubbles: true }));
  }

  /**
   * Handles incoming ActionCable broadcasts.
   * Processes replace, refresh, and reconnect commands.
   * @param {Object} data
   */
  handleBroadcast(data) {
    // Notify connection monitor of activity
    this.notifyConnectionActivity();

    if (data.type === 'replace') {
      // All replace broadcasts are processed; server filters ensure correct targeting
      const target = document.getElementById(data.target);
      if (target) {
        target.outerHTML = data.html;
      }
    } else if (data.type === 'refresh') {
      // Handle refresh commands (e.g., from resume action)
      // Only reload if refresh is for THIS survey (backward compatible with broadcasts missing survey_id)
      if (!data.survey_id || data.survey_id === this.surveyIdValue) {
        window.location.reload();
      }
    } else if (data.type === 'reconnect') {
      // Handle reconnect commands to update subscription with new live state
      this.liveValue = true; // Update the live state
      this.disconnect(); // Disconnect current subscription
      this.connect(); // Reconnect with new state
    }
  }

  /**
   * Handles ActionCable pause event. Updates UI to show paused state.
   * @param {string|number} position
   */
  handlePaused(position) {
    this.liveValue = false;
    this.updateControlsState();
  }

  /**
   * Handles ActionCable resume event. Updates UI to show live state.
   */
  handleResumed() {
    this.liveValue = true;
    this.updateControlsState();
  }

  /**
   * Updates UI elements to reflect the current live/paused state.
   * Can be called by other controllers.
   */
  updateControlsState() {
    // Update any UI elements that need to reflect the live/paused state
    const container = this.element;
    if (container) {
      container.dataset.liveReportingStreamLiveValue = this.liveValue.toString();
    }
  }

  /**
   * Pauses live reporting for this survey (invoked by controls).
   * @param {string|number} position
   */
  pause(position) {
    if (this.subscription) {
      this.subscription.pause(position);
    }
  }

  /**
   * Resumes live reporting for this survey (invoked by controls).
   */
  resume() {
    if (this.subscription) {
      this.subscription.resume();
    }
  }
}
